import { abortTimeout, withAbortListener } from "@redotech/util/abort";
import { Condition } from "@redotech/util/condition";
import { withHidDevice } from "./hid";
import {
  SCALE_USAGE_PAGE,
  ScaleDataInputReport,
  scaleDataInputReportParse,
  ScaleReport,
  ScaleStatus,
  ScaleWeightUnit,
} from "./protocol";
import { Weight, WeightUnit } from "./weight";

/**
 * Device filters for scales.
 */
export const SCALE_FILTERS: HIDDeviceFilter[] = [
  { usagePage: SCALE_USAGE_PAGE },
];

const INPUT_TIMEOUT = Temporal.Duration.from({ seconds: 2 });

/**
 * Read the weight from a scale.
 */
export async function scaleReadWeight(device: HIDDevice): Promise<Weight> {
  const condition = new Condition<ScaleDataInputReport>();

  const listener = (event: HIDInputReportEvent) => {
    const reportData = new ArrayBuffer(event.data.buffer.byteLength + 1);
    const view = new DataView(reportData);
    view.setUint8(0, ScaleReport.SCALE_DATA_REPORT);
    new Uint8Array(reportData).set(new Uint8Array(event.data.buffer), 1);
    const report = scaleDataInputReportParse(reportData);
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

    switch (report.scaleStatus) {
      case ScaleStatus.SCALE_STATUS_UNDER_ZERO:
        report.dataWeight = -report.dataWeight;
      // eslint-disable-next-line no-fallthrough
      case ScaleStatus.SCALE_STATUS_STABLE_AT_CENTER_OF_ZERO:
      case ScaleStatus.SCALE_STATUS_WEIGHT_STABLE:
        condition.resolve(report);
        break;
      case ScaleStatus.SCALE_STATUS_IN_MOTION:
        break;
      default:
        throw new Error("Scale status is invalid");
    }
  };

  device.addEventListener("inputreport", listener);
  let report: ScaleDataInputReport;
  try {
    report = await withAbortListener(
      abortTimeout(INPUT_TIMEOUT),
      condition.reject,
    )(() => withHidDevice(device)(() => condition.result));
  } finally {
    device.removeEventListener("inputreport", listener);
  }

  return weight(report);
}

export function weight(report: ScaleDataInputReport): Weight {
  const amount = report.dataWeight * 10 ** report.dataScaling;
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (report.weightUnit) {
    case ScaleWeightUnit.WEIGHT_UNIT_MILLIGRAM:
      return new Weight(amount / 1000, WeightUnit.GRAM);
    case ScaleWeightUnit.WEIGHT_UNIT_GRAM:
      return new Weight(amount, WeightUnit.GRAM);
    case ScaleWeightUnit.WEIGHT_UNIT_KILOGRAM:
      return new Weight(amount * 1000, WeightUnit.GRAM);
    case ScaleWeightUnit.WEIGHT_UNIT_POUND:
      return new Weight(amount * 16, WeightUnit.OUNCE);
    case ScaleWeightUnit.WEIGHT_UNIT_OUNCE:
      return new Weight(amount, WeightUnit.OUNCE);
  }
  throw new Error(`Unsupported weight unit ${report.weightUnit}`);
}
